import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Edit, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { EditApplicationForm } from '../schemas/EditApplicationSchema'
import { ApplicationPaginated } from './usePaginateApplications'

type PutEditApplicationProps = {
  id: string
  applicationKey: string
  title: string
  description: string
  tag: string[]
  auth: object
  metadata: object | null
  requiresApiUser: boolean
  requiresEntityAccount: boolean
}

async function putEditApplication({ id, ...data }: PutEditApplicationProps) {
  return api.backend.put(`/application/${id}`, data)
}

export function useEditApplication() {
  const { t } = useTranslation('sso')

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    PutEditApplicationProps
  >({
    mutationKey: ['/application/edit'],
    mutationFn: putEditApplication,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: Edit,
          title: t('applications.drawer_edit_application.toast_success.title'),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/application/paginate'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('applications.drawer_edit_application.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('applications.drawer_edit_application.toast_error.description'),
        },
      })
    },
  })

  const handleEditApplication = async (
    application: ApplicationPaginated,
    formData: EditApplicationForm,
  ) => {
    const data: PutEditApplicationProps = {
      id: application.id,
      applicationKey: formData.applicationKey,
      title: formData.title,
      description: formData.description || '',
      tag: formData.tag.map((tag) => tag.value),
      auth: JSON.parse(formData.auth),
      metadata: formData?.metadata ? JSON.parse(formData.metadata) : null,
      requiresApiUser: application.requiresApiUser || false,
      requiresEntityAccount: application.requiresEntityAccount || false,
    }

    return mutateAsync(data)
  }

  return {
    handleEditApplication,
    isEditingApplication: isPending,
  }
}
